import { cloneDeep } from 'lodash';

/**
 * RenderFlow is a simple state machine.
 * GET a variable directly by RenderFlow.[nameofvariable] like RenderFlow.isLoading
 * Quick init by using the constructor as a shorthand for RenderFlow.massInit()
 * Debug via RenderFlow.debug or $vm.RenderFlow.debug()
 *
 * @method set sets this.store relevant object's value key (1st param) to setValue, 2nd param if found, else initializes the relevant object first.
 * @method get Getter for renderflow. returns value if found, Errors if value is not initialized first.
 * @method massSet feed array of RenderFlowStatusDefs to mass set
 * @method massInit mass initialize array of strings as false
 * @method dump get deepcloned copy of store
 * @class RenderFlow
 */
export default class RenderFlow {


  /**
   * Creates an instance of RenderFlow.
   * Creates a shorthand of sorts for massInit.
   * @param {Array<string>} [initArray=[]]
   * @memberof RenderFlow
   */
  constructor(initArray: string[] = []){
    this.massInit(initArray);
  }

  /**
   * Internal store for name value objects
   *
   * @private
   * @type {Array<RenderFlowStatusDefinition>}
   * @memberof RenderFlow
   */
  private store: RenderFlowStatusDefinition[] = [];

  /**
   * sets this.store relevant object's value key (1st param) to setValue, 2nd param
   * if found, else initializes the relevant object first.
   *
   * @param {string} setName
   * @param {Boolean} setValue
   * @returns {Boolean}
   * @memberof RenderFlow
   */
  public set(setName: string, setValue: Boolean): Boolean | Error
  {
    const foundStoreObject = this.store.find(storeObject => storeObject.name === setName);
    // if in store, update value
    if(typeof foundStoreObject !== 'undefined') {
      foundStoreObject.value = setValue;
    } else {

      // typescript hates !this[setName]
      // this is how we can still check
      // if setName not exists on RenderFlow
      if (!this.hasOwnProperty(setName)) {

        // save in store
        // only place where this is allowed!
        this.store.push({
          name: setName,
          value: setValue
        });

        // create dynamic getter && setter
        // pseudo-proxy for get method. Difference:
        // normally: RenderFlow.get('someKey')
        // dynamically: RenderFlow.someKey
        Object.defineProperties(this, {
          [setName]: { // dynamic key
            get() {
              //get via get method so checks are used
              return this.get(setName);
            },
            set(value: boolean){
              // set via set method so checks are used
              this.set(setName, value);
            }
          }
        });
      } else {
        return new Error(`${setName} allready exists on this instance of RenderFlow but is undefined in the store. Please dont key-value store anything on this Class but use RenderFlow.set(key, Boolean) instead.`);
      }

    }

    return true;
  }

  /**
   * massSet is a interface to RenderFlow.set
   * give an array of key: boolean
   *
   * @param {Array<RenderFlowStatusDefinition>} arrayOfRenderFlow
   * @returns {(Boolean | Error)}
   * @memberof RenderFlow
   */
  public massSet(arrayOfRenderFlow: Array<RenderFlowStatusDefinition>) : Boolean | Error
  {
    try {
      arrayOfRenderFlow.forEach(item => this.set(item.name, item.value));
    } catch (error) {
      return error; // see this.set
    }
    return true;

  }

  /**
   * MassInit allows mass false initialisation of values
   *
   * @param {Array<string>} arrayOfNames
   * @returns {(Boolean | Error)}
   * @memberof RenderFlow
   */
  private massInit(arrayOfNames: Array<string>) : Boolean | Error
  {
    try {
      arrayOfNames.forEach(item => this.set(item, false));
    } catch (error) {
      return error; // see this.set
    }
    return true;
  }

  /**
   * Retrieve the entire store of RenderFlow
   * deepcloned... store is private!
   *
   * @returns {Array<RenderFlowStatusDefinition>}
   * @memberof RenderFlow
   */
  public dump() : Array<RenderFlowStatusDefinition>
  {
    return cloneDeep(this.store);
  }

  public debug() : void
  {
    console.table(this.store);
  }

  /**
   * Getter for renderflow. returns value if found,
   * Errors if value is not initialized first.
   *
   * @param {string} findName
   * @returns Boolean or Error
   * @memberof RenderFlow
   */
  public get(findName: string) : Boolean | Error
  {
    const foundRenderFlow = this.store.find(storeObject => storeObject.name === findName);
    if (typeof foundRenderFlow !== 'undefined') {
      return foundRenderFlow.value;
    } else {
      throw new Error(`render flow boolean ${findName} has not been set yet`);
    }
  }

}

interface RenderFlowStatusDefinition {
  name: string;
  value: Boolean;
}
